import { getSharedAudioContext } from './callAudioGraph';

/**
 * 실시간 마이크 입력 레벨(RMS) 측정 유틸리티 (개발 검증용)
 * 
 * [원칙]
 * - callAudioGraph 의 전역 싱글톤 AudioContext 를 재사용합니다.
 * - 새로운 AudioContext 를 절대 생성하지 않습니다.
 * - AudioContext 자체를 close/suspend 하지 않고 노드만 생성 및 해제합니다.
 */

/**
 * MediaStream 에서 실시간 오디오 입력 RMS 레벨 측정 시작
 * 
 * @param {MediaStream} stream - 측정할 로컬 마이크 MediaStream
 * @param {Function} onUpdate - RMS 값이 계산될 때마다 호출될 콜백 (rms: number)
 * @returns {Function} stop - 측정 중지 및 오디오 노드 연결 해제 함수
 */
export function startMicLevelMeter(stream, onUpdate) {
  if (!stream || typeof onUpdate !== 'function') {
    return () => {};
  }

  const audioTracks = stream.getAudioTracks();
  if (audioTracks.length === 0) {
    return () => {};
  }

  let sourceNode = null;
  let analyserNode = null;
  let intervalId = null;
  let isStopped = false;

  try {
    const ctx = getSharedAudioContext();
    if (!ctx) {
      return () => {};
    }

    // MediaStreamSource 및 AnalyserNode 생성
    sourceNode = ctx.createMediaStreamSource(stream);
    analyserNode = ctx.createAnalyser();
    analyserNode.fftSize = 256;

    // 소스 -> 분석기 연결 (스피커 출력 destination 으로는 연결하지 않음)
    sourceNode.connect(analyserNode);

    const bufferLength = analyserNode.fftSize;
    const dataArray = new Float32Array(bufferLength);

    // 약 100ms 주기로 시간 도메인 데이터(PCM)를 읽어 RMS(Root Mean Square) 계산
    intervalId = setInterval(() => {
      if (isStopped || !analyserNode) return;

      try {
        analyserNode.getFloatTimeDomainData(dataArray);

        let sumSquares = 0;
        for (let i = 0; i < bufferLength; i++) {
          const val = dataArray[i];
          sumSquares += val * val;
        }

        const rms = Math.sqrt(sumSquares / bufferLength);
        onUpdate(rms);
      } catch {
        // 노드 해제 과도기 예외 방어
      }
    }, 100);
  } catch (err) {
    console.warn('[micLevelMeter] 분석기 초기화 실패:', err);
    return () => {};
  }

  // 중지 및 노드 정리 함수 반환
  return () => {
    isStopped = true;
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }

    if (sourceNode) {
      try {
        sourceNode.disconnect();
      } catch {}
      sourceNode = null;
    }

    if (analyserNode) {
      try {
        analyserNode.disconnect();
      } catch {}
      analyserNode = null;
    }

    onUpdate(0);
  };
}
